import App from '../App'
import Button from '../components/Button'
import React from 'react'
import { WholesalerContext, WholesalerContextShape } from './WholesalerContext'
import {
  assertAddToCartResponse,
  assertConnectResponse,
  assertGetProductsResponse,
  makeWholesalerApiCall,
} from './shared/WholesalerApi'
import Popup from '../components/Popup'
import Decimal from 'decimal.js-light'
import {
  MutablePackQuantityMap,
  MutablePriceMap,
  PackQuantityMap,
  PriceMap,
  ProductPriceMapValue,
} from '../productPricing/ProductPricingContext'
import { logDebug, logErrorDebug } from '../debug/debugLogging'
import { AddToCartItem, AddToCartResponse, GetProductsResponseItem } from './shared/WholesalerPublicApi'
import { AddToCartArrow, IconCrossGrey } from '../components/icons'
import { BuildConfig } from '../config/BuildConfig'
import ButtonsPopup from '../components/ButtonsPopup'
import { ImagePreloader } from '../components/ImagePreloader'
import { WarningMessagePayload, WholesalerConfiguration } from './shared/WholesalerInternalApi'
import { isWholesalerEntryPointValid } from './EntryPoints'

type WholesalerIntegrationConnectionStatus
  = { status: 'error' }
  | { status: 'connected', config: WholesalerConfiguration }

export function assertUnknown(input: unknown): asserts input is unknown {
  // always valid
}

const MessageTimeoutShort = 5 * 1000
const MessageTimeoutLong = 30 * 1000

const WholesalerApi = {
  connect: () => makeWholesalerApiCall('IconicBuilder.Wholesaler.Connect', undefined, assertConnectResponse, MessageTimeoutShort),

  close: () => makeWholesalerApiCall('IconicBuilder.Wholesaler.Close', undefined, assertUnknown, MessageTimeoutShort),

  goToCart: () => makeWholesalerApiCall('IconicBuilder.Wholesaler.GoToCart', undefined, assertUnknown, MessageTimeoutShort),

  getPrices: (data: {itemNos: string[]}) => makeWholesalerApiCall('IconicBuilder.Wholesaler.Price', data, assertGetProductsResponse, MessageTimeoutLong),

  addToCart: (data: {items: AddToCartItem[]}) => makeWholesalerApiCall('IconicBuilder.Wholesaler.AddToCart', data, assertAddToCartResponse, MessageTimeoutLong),

  warningMessage: (data: WarningMessagePayload) => makeWholesalerApiCall('IconicBuilder.Wholesaler.Warning', data, assertUnknown, MessageTimeoutShort),
}

export const WholesalerIntegrationAppWrapper = () => {
  const [pricing, setPricing] = React.useState<Readonly<Record<string, GetProductsResponseItem | null>>>({})
  const [pricingLoadingCount, setPricingLoadingCount] = React.useState(0)

  const [confirmCloseShown, setConfirmCloseShown] = React.useState(false)
  const [connectionStatus, setConnectionStatus] = React.useState<WholesalerIntegrationConnectionStatus | null>(null)

  const priceMap: PriceMap = React.useMemo(() => {
    const map: MutablePriceMap = { }
    Object.keys(pricing).forEach(key => {
      const priceString = pricing[key]?.price ?? null
      let priceValue: ProductPriceMapValue

      if (priceString) {
        priceValue = new Decimal(priceString)
      }

      map[key] = priceValue
    })

    return map
  }, [pricing])

  const packQuantityMap: PackQuantityMap = React.useMemo(() => {
    const map: MutablePackQuantityMap = { }
    Object.keys(pricing).forEach(key => {
      map[key] = pricing[key]?.packQuantity ?? null
    })

    return map
  }, [pricing])

  React.useEffect(() => {
    WholesalerApi.connect()
      .then(it => {
        logDebug('WholesalerIntegration', 'connected to wholesaler', it)

        // typeof check shouldn't be necessary, but the linter was getting a nonsense type error
        const entryPoint = typeof it.configuration.entryPoint === 'string' ? it.configuration.entryPoint : null

        if (entryPoint !== null && !isWholesalerEntryPointValid(entryPoint)) {
          void WholesalerApi.warningMessage({ message: `unrecognised entryPoint "${entryPoint}", ignoring` })
        }

        setConnectionStatus({ status: 'connected', config: it.configuration })

      })
      .catch(it => {
        logErrorDebug('WholesalerIntegration', 'error connecting to wholesaler', it)
        setConnectionStatus({ status: 'error' })
      })
  }, [])

  if (connectionStatus?.status === 'error') {
    return (
      <Popup onClose={() => { return }}>
        <div className="title">Something went wrong</div>
        <div className="buttonRow">
          <Button
            buttonStyle="redStroke"
            onClick={() => {
              WholesalerApi.close()
                .catch(console.error)
            }}
          >
            Close builder
          </Button>
        </div>
      </Popup>
    )
  }

  if (connectionStatus?.status === 'connected') {
    const fetchWholesalerPrice = (itemNos: string[]) => {
      const filteredItemNos = itemNos.filter(itemNo => pricing[itemNo] === undefined)

      if (filteredItemNos.length > 0) {
        setPricing(current => {
          const prices = { ...current }

          filteredItemNos.forEach(itemNo => {
            prices[itemNo] = null
          })

          return prices
        })

        logDebug('WholesalerPricing', 'requesting wholesale pricing for', filteredItemNos)

        setPricingLoadingCount(current => current + 1)

        void WholesalerApi.getPrices( { itemNos: filteredItemNos })
          .then(res => {
            assertGetProductsResponse(res)

            setPricing(current => {
              const pricingCopy = { ...current }

              logDebug('WholesalerPricing', 'got wholesale pricing', res.items)

              res.items.forEach(item => {
                pricingCopy[item.itemNo] = item
              })

              return pricingCopy
            })
          })
          .catch(error => {
            logErrorDebug('WholesalerPricing', 'error getting wholesale pricing', error)
          })
          .finally(() => {
            setPricingLoadingCount(current => current - 1)
          })
      }
    }

    const copy = BuildConfig.brand.copy.Wholesaler

    const wholesalerContextValue: WholesalerContextShape = {
      brandName: connectionStatus.config.brandName,
      brandLogo: connectionStatus.config.brandLogo,
      priceIncludesGst: connectionStatus.config.priceIncludesGst,
      entryPoint: connectionStatus.config.entryPoint ?? null,
      pricing,
      priceMap,
      isFetchingPricing: pricingLoadingCount !== 0,
      packQuantityMap,
      fetchWholesalerPrice,
      addToWholesalerCart(items: { itemNo: string, quantity: number }[]): Promise<AddToCartResponse> {
        return WholesalerApi.addToCart({ items })
          .then(res => {
            logDebug('WholesalerApi.addToCart', res)
            assertAddToCartResponse(res)
            return res
          })
      },
      goToWholesalerCart(): Promise<void> {
        return WholesalerApi.goToCart()
          .catch(e => {
            logErrorDebug('error navigating to wholesaler cart')
          })
      }
    }

    return (
      <WholesalerContext.Provider value={wholesalerContextValue}>
        <div className="AppEmbedded">
          <div className="AppEmbedded-buttonHeader">
            <Button
              buttonStyle="white"
              onClick={() => {
                setConfirmCloseShown(true)
              }}
            >
              Close
              <img src={IconCrossGrey} alt="Cancel" style={{ marginLeft: '10px' }} />
            </Button>
          </div>
          <App/>
        </div>

        {confirmCloseShown && (
          <ButtonsPopup
            orientation="row"
            title={copy.ClosePopup.Title}
            actions={[
              {
                onClick: () => {
                  setConfirmCloseShown(false)
                },
                label: copy.ClosePopup.CancelButton,
                style: 'default',
              },
              {
                style: 'redStroke',
                onClick: () => {
                  WholesalerApi.close()
                    .catch(console.error)
                },
                label: copy.ClosePopup.ConfirmButton,
              },
            ]}
          >
            {copy.ClosePopup.Body}
          </ButtonsPopup>
        )}
        <ImagePreloader
          srcs={[
            wholesalerContextValue.brandLogo,
            AddToCartArrow,
          ]}
        />
      </WholesalerContext.Provider>
    )
  }

  return null
}